import React, { useState, useEffect } from "react";
import classes from "./Journal.module.css";
import api from "../../../Components/API/Api";
import { Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import crop from "../../../Assets/repoort.png";
import verified from "../../../Assets/verified.svg";

function JournalModal({ isOpen, onClose, onSaveSuccess }) {
  const [rows, setRows] = useState([
    { accountId: "", accountDisplay: "", description: "", debit: "", credit: "", showDropdown: false }
  ]);
  const [reference, setReference] = useState("");
  const [loadingRef, setLoadingRef] = useState(false);
  const [transactionDate, setTransactionDate] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  // currencies state
  const [currencies, setCurrencies] = useState([]);
  const [currenciesLoading, setCurrenciesLoading] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const handlePrinter = () => window.print();

  const fetchReference = async () => {
    setLoadingRef(true);
    try {
      const res = await api.get(`/v1/generate-journal-code`);
      if (res?.data) {
        const payload = res.data;
        const d =
          payload?.data ??
          payload?.code ??
          payload?.reference ??
          payload?.number ??
          payload?.data?.code;
        let ref = "";
        if (typeof d === "string" || typeof d === "number") {
          ref = d;
        } else if (d && typeof d === "object") {
          ref = d.code || d.reference || d.number || "";
        }
        setReference(ref ? String(ref) : "");
      }
    } catch (error) {
      console.error("Error fetching journal reference:", error);
    } finally {
      setLoadingRef(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchReference();
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchAccounts = async () => {
      setAccountsLoading(true);
      try {
        const res = await api.get(`/v1/account`);
        const payload = res?.data;
        let raw = payload?.data ?? payload;
        if (raw && typeof raw === "object" && Array.isArray(raw.data)) {
          raw = raw.data;
        }
        const list = Array.isArray(raw) ? raw : [];
        setAccounts(list);
      } catch (err) {
        console.error("Error fetching accounts:", err);
        setAccounts([]);
      } finally {
        setAccountsLoading(false);
      }
    };

    if (isOpen) {
      fetchAccounts();
    }
  }, [isOpen]);

  // fetch currencies when modal opens
  useEffect(() => {
    const fetchCurrencies = async () => {
      setCurrenciesLoading(true);
      try {
        const res = await api.get(`/v1/fetch-all-currencies`);
        const payload = res?.data;
        const list = payload?.data ?? payload ?? [];
        setCurrencies(Array.isArray(list) ? list : []);
        if (Array.isArray(list) && list.length > 0) {
          setSelectedCurrency(list[0]);
        }
      } catch (err) {
        console.error("Error fetching currencies:", err);
        setCurrencies([]);
      } finally {
        setCurrenciesLoading(false);
      }
    };
    if (isOpen) fetchCurrencies();
  }, [isOpen]);

  if (!isOpen) return null;

  const addLine = () => {
    setRows(prev => [
      ...prev,
      { accountId: "", accountDisplay: "", description: "", debit: "", credit: "", showDropdown: false },
    ]);
  };

  const duplicateRow = (index) => {
    setRows(prev => {
      const newRows = [...prev];
      const emptyRow = { accountId: "", accountDisplay: "", description: "", debit: "", credit: "", showDropdown: false };
      newRows.splice(index + 1, 0, emptyRow);
      return newRows;
    });
  };

  const removeRow = (index) => {
    setRows(prev => {
      if (prev.length === 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleChange = (index, field, value) => {
    setRows(prev => {
      if (field === "showDropdown") {
        const newRows = [...prev];
        newRows[index] = { ...newRows[index], showDropdown: value };
        return newRows;
      }

      const newRows = [...prev];
      const row = { ...newRows[index] };

      if (field === "debit") {
        row.debit = value;
        if (value && parseFloat(value) > 0) {
          row.credit = "0";
        }
      } else if (field === "credit") {
        row.credit = value;
        if (value && parseFloat(value) > 0) {
          row.debit = "0";
        }
      } else if (field === "account") {
        row.accountId = value?.id ?? "";
        row.accountDisplay = `${value?.gl_code ?? value?.code ?? ""} - ${value?.gl_name ?? value?.name ?? ""}`;
      } else {
        row[field] = value;
      }

      newRows[index] = row;
      return newRows;
    });
  };

  const totalDebit = rows.reduce(
    (sum, r) => sum + (parseFloat(r.debit) || 0),
    0
  );
  const totalCredit = rows.reduce(
    (sum, r) => sum + (parseFloat(r.credit) || 0),
    0
  );

  const handleSaveJournal = async () => {

    if (!selectedCurrency) {
      Swal.fire({
        icon: "warning",
        title: "Missing Currency",
        text: "Please select a currency before submitting.",
        confirmButtonText: "Okay",
        allowOutsideClick: false,
        didOpen: () => {
          const swalContainer = document.querySelector('.swal2-container');
          if (swalContainer) swalContainer.style.zIndex = '99999';
        },
      });
      return;
    }

    if (!transactionDate) {
      Swal.fire({
        icon: "warning",
        title: "Missing Date",
        text: "Please select a transaction date.",
        confirmButtonText: "Okay",
        allowOutsideClick: false,
        didOpen: () => {
          const swalContainer = document.querySelector('.swal2-container');
          if (swalContainer) swalContainer.style.zIndex = '99999';
        },
      });
      return;
    }

    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      const hasDebit = r.debit !== "" && !isNaN(parseFloat(r.debit)) && parseFloat(r.debit) > 0;
      const hasCredit = r.credit !== "" && !isNaN(parseFloat(r.credit)) && parseFloat(r.credit) > 0;
      if (!r.accountId) {
        Swal.fire({
          icon: "warning",
          title: "Missing Account",
          text: `Please select an account for row ${i + 1}.`,
          confirmButtonText: "Okay",
          allowOutsideClick: false,
          didOpen: () => {
            const swalContainer = document.querySelector('.swal2-container');
            if (swalContainer) swalContainer.style.zIndex = '99999';
          },
        });
        return;
      }
      if (!r.description || String(r.description).trim() === "") {
        Swal.fire({
          icon: "warning",
          title: "Missing Description",
          text: `Please add a description for row ${i + 1}.`,
          confirmButtonText: "Okay",
          allowOutsideClick: false,
          didOpen: () => {
            const swalContainer = document.querySelector('.swal2-container');
            if (swalContainer) swalContainer.style.zIndex = '99999';
          },
        });
        return;
      }
      if (!(hasDebit || hasCredit)) {
        Swal.fire("Missing Amount", `Please enter a debit or credit amount for row ${i + 1}.`, "warning");
        return;
      }
      if (hasDebit && hasCredit) {
        Swal.fire("Invalid Row", `Row ${i + 1} has both debit and credit. Only one is allowed.`, "warning");
        return;
      }
    }

    const account_id = rows.map(r => String(r.accountId));
    const direction = rows.map(r => {
      const hasDebit = r.debit !== "" && !isNaN(parseFloat(r.debit)) && parseFloat(r.debit) > 0;
      return hasDebit ? "1" : "2";
    });
    const description = rows.map(r => r.description ?? "");
    const amount = rows.map(r => {
      const hasDebit = r.debit !== "" && !isNaN(parseFloat(r.debit)) && parseFloat(r.debit) > 0;
      return hasDebit ? parseFloat(r.debit) : parseFloat(r.credit || 0);
    });

    const companyId =
      localStorage.getItem("company_id") ||
      localStorage.getItem("companyId") ||
      localStorage.getItem("activeCompanyId");

    if (!companyId) {
      Swal.fire({
        icon: "warning",
        title: "Missing Company",
        text: "Company ID is required to submit this journal. Please select a company and try again.",
        confirmButtonText: "Okay",
        allowOutsideClick: false,
        didOpen: () => {
          const swalContainer = document.querySelector('.swal2-container');
          if (swalContainer) swalContainer.style.zIndex = '99999';
        },
      });
      return;
    }

    const currencyToSend = selectedCurrency && (selectedCurrency.id !== undefined && selectedCurrency.id !== null)
      ? Number(selectedCurrency.id)
      : (selectedCurrency.code ?? selectedCurrency);

    const payload = {
      company_id: companyId,
      jv_number: reference,
      all_debit: totalDebit,
      all_credit: totalCredit,
      account_id,
      direction,
      transaction_date: transactionDate,
      description,
      amount,
      currency: currencyToSend,
    };

    setIsSaving(true);
    try {
      const res = await api.post(`/v1/journal/post-entries`, payload);
      Swal.fire({
        imageUrl: verified,
        title: "Success!",
        text: res.data.message || "Journal entry saved successfully!",
        confirmButtonText: "Okay",
        imageWidth: 48,
        imageHeight: 48,
        customClass: {
          popup: classes.myText,
          title: classes.myTitle,
          confirmButton: classes.myButton,
        },
        allowOutsideClick: false,
        didOpen: () => {
          const swalContainer = document.querySelector('.swal2-container');
          if (swalContainer) swalContainer.style.zIndex = '99999';
        },
      });

      if (typeof onSaveSuccess === 'function') {
        onSaveSuccess();
      }

      // reset form rows and date
      setRows([{ accountId: "", accountDisplay: "", description: "", debit: "", credit: "", showDropdown: false }]);
      setTransactionDate("");

      // fetch a fresh reference so the user can create another journal immediately
      try {
        await fetchReference();
      } catch (err) {
        // swallow — reference refresh is best-effort
        console.error('Failed to refresh journal reference after save:', err);
      }
    } catch (error) {
      console.error("Save journal error:", error);

      let msg = "Network error";
      if (error?.response?.data) {
        const data = error.response.data;
        if (data.message && typeof data.message === "object") {
          const parts = [];
          Object.keys(data.message).forEach(k => {
            const val = data.message[k];
            if (Array.isArray(val)) parts.push(`${k}: ${val.join(", ")}`);
            else parts.push(`${k}: ${String(val)}`);
          });
          if (parts.length) msg = parts.join(" | ");
          else msg = data.message || JSON.stringify(data);
        } else {
          msg = data.message || data.error || JSON.stringify(data);
        }
      } else if (error.message) {
        msg = error.message;
      }

      Swal.fire({
        imageUrl: crop,
        imageWidth: 48,
        imageHeight: 48,
        title: "Failed!",
        confirmButtonText: "Okay",
        text: msg,
        customClass: {
          popup: classes.myText,
          title: classes.myTitle,
          confirmButton: classes.myDeclineButton,
        },
        allowOutsideClick: false,
        didOpen: () => {
          const swalContainer = document.querySelector('.swal2-container');
          if (swalContainer) swalContainer.style.zIndex = '99999';
        },
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={classes.modalOverlay}>
      <div className={classes.modalContent}>
        <h3 className={classes.title}>Journal Entry </h3>
        <p className={classes.subtitle}>
          Add multiple journal lines (Account, Description, Debit, Credit). Totals are calculated and validated before submit.
        </p>

        <div className={classes.topRow}>
          <div className={classes.inputGroup}>
            <label>Journal Reference</label>
            <div className={classes.refContainer}>
              {loadingRef ? (
                <div className={classes.spinnerWrapper}>
                  <Spinner animation="border" size="sm" />
                </div>
              ) : (
                <input
                  type="text"
                  value={reference}
                  readOnly
                  placeholder="Fetching reference..."
                />
              )}
            </div>
          </div>
          <div className={classes.inputGroup}>
            <label>Date</label>
            <input
              type="date"
              value={transactionDate}
              onChange={e => setTransactionDate(e.target.value)}
              required
            />
          </div>
          <div className={classes.inputGroup}>
            <label>Currency</label>
            <select
              value={selectedCurrency ? (selectedCurrency.id ?? selectedCurrency.code ?? "") : ""}
              onChange={(e) => {
                const val = e.target.value;
                const found = currencies.find(c => String(c.id) === val) || currencies.find(c => (c.code ?? "") === val) || null;
                setSelectedCurrency(found);
              }}
            >
              {!selectedCurrency && <option value="">Select currency</option>}
              {currenciesLoading && <option value="">Loading currencies...</option>}
              {!currenciesLoading && currencies.map(c => (
                <option key={c.id ?? c.code ?? JSON.stringify(c)} value={c.id ?? c.code}>
                  {c.code ? `${c.code} - ${c.name ?? c.display ?? ""}` : (c.name ?? c.id ?? "")}
                </option>
              ))}
            </select>
          </div>
        </div>

        <table className={classes.table}>
          <thead>
            <tr>
              <th>#</th>
              <th>Account Code / Name</th>
              <th>Description</th>
              <th>Debit</th>
              <th>Credit</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td className={classes.accountCell}>
                  <div className={classes.searchableDropdown}>
                    <input
                      type="text"
                      value={row.accountDisplay}
                      onChange={(e) => {
                        const value = e.target.value;
                        handleChange(index, "accountDisplay", value);
                      }}
                      onFocus={() => handleChange(index, "showDropdown", true)}
                      placeholder="Search or type account..."
                      className={classes.accountInput}
                    />
                    {row.showDropdown && (
                      <div className={classes.dropdownList}>
                        {accountsLoading ? (
                          <div className={classes.dropdownLoading}>
                            <Spinner animation="border" size="sm" />
                          </div>
                        ) : (
                          accounts
                            .filter((acc) => {
                              const search = row.accountDisplay?.toLowerCase() || "";
                              const text = `${acc.gl_code ?? acc.code ?? ""} ${acc.gl_name ?? acc.name ?? ""}`.toLowerCase();
                              return text.includes(search);
                            })
                            .map((acc) => (
                              <div
                                key={acc.id}
                                className={classes.dropdownItem}
                                onClick={() => {
                                  handleChange(index, "account", acc);
                                  handleChange(index, "showDropdown", false);
                                }}
                              >
                                {`${acc.gl_code ?? acc.code ?? ""} - ${acc.gl_name ?? acc.name ?? ""}`}
                              </div>
                            ))
                        )}
                      </div>
                    )}
                  </div>
                </td>

                <td>
                  <input
                    type="text"
                    value={row.description}
                    onChange={e => handleChange(index, "description", e.target.value)}
                    placeholder="Narration / description"
                  />
                </td>
                <td>
                  <input
                    type="number"
                    className={classes.debitInput}
                    value={row.debit}
                    onChange={e => handleChange(index, "debit", e.target.value)}
                    disabled={parseFloat(row.credit) > 0}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    className={classes.creditInput}
                    value={row.credit}
                    onChange={e => handleChange(index, "credit", e.target.value)}
                    disabled={parseFloat(row.debit) > 0}
                  />
                </td>
                <td>
                  <button onClick={() => duplicateRow(index)} className={classes.linkBtn}>
                    Duplicate
                  </button>
                  <button onClick={() => removeRow(index)} className={classes.removeBtn}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className={classes.actionRow}>
          <button className={classes.addLineBtn} onClick={addLine}>
            + Add Line
          </button>
        </div>

        <div className={classes.totalRow}>
          <div className={classes.totalBox}>
            <span>Total Debit</span>
            <strong>{totalDebit.toFixed(2)}</strong>
          </div>
          <div className={classes.totalBox}>
            <span>Total Credit</span>
            <strong>{totalCredit.toFixed(2)}</strong>
          </div>
        </div>

        <div className={classes.footerButtons}>
          <button
            className={classes.resetBtn}
            onClick={() =>
              setRows([{ accountId: "", accountDisplay: "", description: "", debit: "", credit: "", showDropdown: false }])
            }
          >
            Reset
          </button>
          <button className={classes.printBtn} onClick={handlePrinter}>Print</button>
          <button
            className={classes.submitBtn}
            onClick={handleSaveJournal}
            disabled={isSaving}
          >
            {isSaving ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Spinner animation="border" size="sm" />
                Saving...
              </span>
            ) : (
              "Submit Journal"
            )}
          </button>
        </div>

        <button className={classes.closeBtn} onClick={onClose}>
          ×
        </button>
      </div>
    </div>
  );
}

export default JournalModal;
